/**
 * Rehype plugin: marks headings that follow a paragraph.
 *
 * What: Walks the top-level HAST children (skipping non-elements) and marks
 *       any h1-h6 whose preceding element sibling is a <p>.
 * Why: CSS sibling selectors (p + h2) are unreliable in MUI/Emotion because the
 *       generated class specificity can conflict with parent-level nested selectors.
 *       Marking nodes at the AST level lets React components apply conditional margins
 *       directly, which is deterministic across renders and page refreshes.
 * When: Update if heading spacing logic changes or new sibling-dependent styles are needed.
 *
 * IMPORTANT: Keep this plugin AFTER rehype-sanitize in the plugin chain.
 */

import type { Element, Root } from 'hast';

const HEADING_TAG = /^h[1-6]$/;

export function rehypeHeadingSiblings() {
  return (tree: Root): void => {
    const { children } = tree;
    if (!children) return;

    let prevElement: Element | null = null;
    for (const node of children) {
      if (node.type !== 'element') continue;

      if (HEADING_TAG.test(node.tagName) && prevElement?.tagName === 'p') {
        node.properties = node.properties || {};
        node.data = node.data || {};
        (node.data as Record<string, unknown>).afterParagraph = true;
        node.properties['dataAfterP'] = 'true';
      }

      prevElement = node;
    }
  };
}
